import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { lifestyleApi } from '../apiService/api';
import { getAppParams, getUrlParams } from '../apiService/timespam';
import GOQiiLoader from '../Components/GOQiiLoader';
import CognitiveAgeCardV3 from '../Components/Games/CognitiveAgeCardV3';
import './Theme.css';
import './GamesForGood.css';
import './GamesList.css';

// ─── DESIGN SWITCH ────────────────────────────────────────────────────────────
// Change this one value to toggle between themes:
//   'neo'  → Neomorphism (raised/sunken shadows, dark background)
//   'flat' → Flat clean design (screenshot style, light cards)
const DESIGN_THEME = 'flat';

// ─── SINGLE BRAND COLOUR ──────────────────────────────────────────────────────
// We intentionally avoid the per-game API colors to keep the page cohesive.
// All accents (ring arc, score, progress bar, etc.) use this one brand green.
const BRAND_COLOR = '#35b400';
// ─────────────────────────────────────────────────────────────────────────────

// ─── "Played N ago" ───────────────────────────────────────────────────────────
// Calendar-accurate years / months / days between the played date and today
// (whole days -- time of day is ignored), e.g. "1 year 2 months 5 days".
const addMonthsClamped = (date, months) => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDay));
  return target;
};

const getElapsed = (dateStr) => {
  if (!dateStr) return null;
  const played = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(played.getTime())) return null;

  const now = new Date();
  const start = new Date(played.getFullYear(), played.getMonth(), played.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Clock skew can put the played date slightly in the future -- treat as today.
  if (end <= start) return { years: 0, months: 0, days: 0 };

  let totalMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (addMonthsClamped(start, totalMonths) > end) totalMonths -= 1;
  const days = Math.round((end - addMonthsClamped(start, totalMonths)) / 86400000);

  return { years: Math.floor(totalMonths / 12), months: totalMonths % 12, days };
};

const formatPlayedAgo = (dateStr, t) => {
  const elapsed = getElapsed(dateStr);
  if (!elapsed) return '';
  const parts = [];
  if (elapsed.years)  parts.push(t('games.elapsed.year',  { count: elapsed.years }));
  if (elapsed.months) parts.push(t('games.elapsed.month', { count: elapsed.months }));
  if (elapsed.days)   parts.push(t('games.elapsed.day',   { count: elapsed.days }));
  if (!parts.length) return t('games.playedToday');
  return t('games.playedAgo', { when: parts.join(t('games.elapsed.join')) });
};

const RING_SIZE    = 96;
const TRACK_W      = 4;
const ARC_W        = 4;
const RADIUS       = (RING_SIZE - ARC_W * 2) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CX = RING_SIZE / 2;
const CY = RING_SIZE / 2;

// Unplayed / zero-score games get an "open" ring: a dotted loop instead of the
// solid grey track, so it reads as unfinished at a glance.
const TODO_DOT_COUNT = 24;
const TODO_DASH_ARRAY = `0.01 ${CIRCUMFERENCE / TODO_DOT_COUNT - 0.01}`;

const CircleRing = ({ score, bgColor, gid, todo }) => {
  const hasScore = score !== null && score !== undefined;
  const offset   = hasScore
    ? CIRCUMFERENCE - (score / 100) * CIRCUMFERENCE
    : CIRCUMFERENCE;

  const safeId   = (gid || 'x').replace(/[^a-zA-Z0-9]/g, '');
  const concaveId = `concave-${safeId}`;
  const convexId  = `convex-${safeId}`;

  return (
    <svg
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      className="ring-svg"
      style={{ overflow: 'visible' }}
    >
      <defs>
        <filter id={concaveId} x="-40%" y="-40%" width="180%" height="180%">
          <feOffset in="SourceAlpha" dx="2"  dy="2"  result="da-offset" />
          <feGaussianBlur in="da-offset" stdDeviation="1.5" result="da-blur" />
          <feFlood floodColor="#a8a8a8" floodOpacity="1" result="da-color" />
          <feComposite in="da-color" in2="da-blur" operator="in" result="dark-inner" />
          <feOffset in="SourceAlpha" dx="-2" dy="-2" result="la-offset" />
          <feGaussianBlur in="la-offset" stdDeviation="1.5" result="la-blur" />
          <feFlood floodColor="#ffffff" floodOpacity="1" result="la-color" />
          <feComposite in="la-color" in2="la-blur" operator="in" result="light-inner" />
          <feMerge>
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="dark-inner" />
            <feMergeNode in="light-inner" />
          </feMerge>
        </filter>
        <filter id={convexId} x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="2" dy="3" stdDeviation="2.5"
            floodColor="rgba(0,0,0,0.28)" floodOpacity="1" result="drop" />
          <feMerge>
            <feMergeNode in="drop" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <circle
        className={`ring-track${todo ? ' ring-track--todo' : ''}`}
        cx={CX} cy={CY} r={RADIUS}
        fill="none"
        stroke="#d8d8d8"
        strokeWidth={TRACK_W}
        strokeLinecap={todo ? 'round' : undefined}
        strokeDasharray={todo ? TODO_DASH_ARRAY : undefined}
        filter={`url(#${concaveId})`}
      />

      {hasScore && score > 0 && (
        <>
          <circle
            className="ring-progress"
            cx={CX} cy={CY} r={RADIUS}
            fill="none"
            stroke={bgColor}
            strokeWidth={ARC_W}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${CX} ${CY})`}
            filter={`url(#${convexId})`}
          />
          <circle
            className="ring-gloss"
            cx={CX} cy={CY} r={RADIUS}
            fill="none"
            stroke="rgba(255,255,255,0.45)"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${CX} ${CY})`}
          />
        </>
      )}
    </svg>
  );
};

const SkeletonCard = () => (
  <div className="game-card skeleton-card">
    <div className="skeleton skeleton-title-top" />
    <div className="skeleton circle-skeleton" />
    <div className="skeleton skeleton-score" />
    <div className="skeleton skeleton-category" />
    <div className="skeleton skeleton-sub" />
  </div>
);

const GamesList = () => {
  const { t } = useTranslation();
  const appParams = getAppParams();
  const { theme, lang } = getUrlParams();
  const [games, setGames] = useState([]);
  const [lastPlayed, setLastPlayed] = useState(null);
  const [recommendGame, setRecommendGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navigating, setNavigating] = useState(false);

  // Banner + Cognitive Age card previously only showed when arriving from the
  // assessment/games-4-good flow (showBanner=1). Per product decision the
  // Cognitive Age card (now its own component) always shows on this page;
  // the banner itself is retired -- see the commented-out block below.
  // const qs = window.location.search;
  // const showBanner = new URLSearchParams(qs).get('showBanner') === '1';

  useEffect(() => {
    document.documentElement.className = theme === 'dark' ? 'dark' : '';
    if (lang === 'ar') document.documentElement.dir = 'rtl';
  }, [theme, lang]);

  // Prevent body-level scroll; container handles its own overflow
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Returning via the native back button restores this page from the
  // browser/webview's back-forward cache instead of remounting it, so React
  // state (including `navigating`, set right before the hard redirect to
  // the game) comes back frozen as it was when we left — leaving the loader
  // stuck on screen forever unless we explicitly clear it here. `pageshow`
  // with `event.persisted` is the standard signal for this, but mobile
  // WebViews (Android especially) are inconsistent about firing it, so
  // `visibilitychange`/`focus` are wired up too as a safety net — clearing
  // an already-false `navigating` is a harmless no-op.
  useEffect(() => {
    const clearNavigating = () => setNavigating(false);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') clearNavigating();
    };
    window.addEventListener('pageshow', clearNavigating);
    window.addEventListener('focus', clearNavigating);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('pageshow', clearNavigating);
      window.removeEventListener('focus', clearNavigating);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // const handleBannerClick = () => {
  //   const p = new URLSearchParams(qs);
  //   p.delete('showBanner');
  //   window.location.href = `/all-games-play?${p.toString()}`;
  // };

  // Cognitive age fetch lives in <CognitiveAgeCardV3 /> (Components/Games/CognitiveAgeCardV3.jsx)
  // -- it fetches its own data and always renders, regardless of how this page was reached.
  // (CognitiveAgeCard.jsx is the retired v1, kept in the repo but no longer used.)

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const res = await lifestyleApi.fetchGamesV2(appParams);
        if (res.data?.code === 200) {
          setGames(res.data.data.games || []);
          setLastPlayed(res.data.data.lastPlayed || null);
          setRecommendGame(res.data.data.recommendGame || null);
        } else {
          setError(t('games.error'));
        }
      } catch {
        setError(t('games.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  // FSN 61 just tells the app to open the given url in a fresh browser
  // instance — it doesn't care which host the url is on, so the API's url
  // is passed through as-is here (unlike navigateWithFai, which rewrites
  // bare goqii.com links to lifestyle1.goqii.com for links that belong to
  // this app). The older games genuinely live on goqii.com itself, so
  // rewriting their host would point fsn 61 at the wrong page.
  const handleGameClick = (url, title) => {
    setNavigating(true);
    const encodedFaiValue = encodeURIComponent(JSON.stringify({ url, title }));
    window.location.href = `https://goqii.com/app?fsn=61&fssn=0&fai=${encodedFaiValue}`;
  };

  const lastPlayedGame = lastPlayed ? games.find(g => g.gid === lastPlayed.gid) : null;

  // Short-category cards first; long (wrapping) cards cluster at the end in pairs.
  // If the count of long-category cards is odd, the last card is alone at the very end.
  // The recommended game is then always pulled to the very front, on top of that order.
  const sortedGames = useMemo(() => {
    const byCategoryLength = [...games].sort((a, b) => (a.category?.length || 0) - (b.category?.length || 0));
    if (!recommendGame) return byCategoryLength;
    const recommendedIndex = byCategoryLength.findIndex(g => g.gameId === recommendGame.gameId);
    if (recommendedIndex <= 0) return byCategoryLength;
    const [recommended] = byCategoryLength.splice(recommendedIndex, 1);
    return [recommended, ...byCategoryLength];
  }, [games, recommendGame]);

  const containerClass = `games-container theme-${DESIGN_THEME}`;

  if (loading) {
    return (
      <div className={containerClass}>
        <div className="games-header">
          <div>
            <div className="skeleton skeleton-heading" />
            <div className="skeleton skeleton-sub-heading" />
          </div>
        </div>
        <div className="skeleton lp-hero-skeleton" />
        <div className="games-grid">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        <p className="games-error">{error}</p>
      </div>
    );
  }

  return (
    <div className={containerClass}>

      <GOQiiLoader visible={navigating} size={80} />

      {/* Cognitive Age banner — retired, was only shown when arriving from games-4-good or all-games-play
      {showBanner && (
        <div className="gfg-banner-wrap" style={{ cursor: 'pointer', marginBottom: '10px' }} onClick={handleBannerClick}>
          <img
            src="https://storecdn.goqii.com/emailer/40241_1706934474.png"
            className="gfg-banner-img"
            alt="Know your cognitive age"
          />
        </div>
      )}
      */}

      {/* Cognitive Age card — now always shown on this page, regardless of entry point */}
      <CognitiveAgeCardV3 />

      {/* Page Header */}
      {/* <div className="games-header">
        <div>
          <h1 className="games-heading">Games4Good</h1>
          <p className="games-sub">{t('games.subtitle')}</p>
        </div>
        <div className="games-count-badge">{t('games.gamesCount', { count: games.length })}</div>
      </div> */}

      {/* ── Last Played Hero (commented out — replaced by Recommend card below) ──
      {lastPlayed && lastPlayedGame && (
        <div className="lp-hero" onClick={() => handleGameClick(lastPlayedGame.url)}>
          <div className="lp-hero-left">
            <span className="lp-hero-label">{t('games.continuePlaying')}</span>
            <h2 className="lp-hero-title">{lastPlayed.gameName}</h2>
            <div className="lp-hero-bar-wrap">
              <div className="lp-hero-bar-bg">
                <div
                  className="lp-hero-bar-fill"
                  style={{ width: `${lastPlayed.scoreOutOf100}%`, background: BRAND_COLOR }}
                />
              </div>
              <span className="lp-hero-score">
                {lastPlayed.scoreOutOf100}/100
              </span>
            </div>
            <div className="lp-hero-footer">
              <span className="lp-hero-date">{formatDate(lastPlayed.playedAt)}</span>
              <button
                className="lp-hero-btn"
                onClick={(e) => { e.stopPropagation(); handleGameClick(lastPlayedGame.url); }}
              >
                {t('games.playAgain')}
              </button>
            </div>
          </div>
          <div className="lp-hero-right">
            <img src={lastPlayedGame.icon} alt={lastPlayed.gameName} className="lp-hero-img" />
          </div>
        </div>
      )}
      ── */}

      {/* Recommended Game Hero */}
      {recommendGame && (
        <div className="rec-hero" onClick={() => handleGameClick(recommendGame.urlSlug, recommendGame.gameName)}>
          <div className="rec-hero-left">
            <span className="rec-hero-label">⚡ Recommended for you</span>
            <h2 className="rec-hero-title">{recommendGame.gameName}</h2>
            <span 
            // className="rec-hero-category"
            className="rec-hero-category"
            >{recommendGame.category}</span>
            {/* <p className="rec-hero-meta">
              {recommendGame.unplayedCount} of {recommendGame.totalGames} games unplayed
            </p> */}
            <button
              className="rec-hero-btn"
              onClick={(e) => { e.stopPropagation(); handleGameClick(recommendGame.urlSlug, recommendGame.gameName); }}
            >
              Play
            </button>
          </div>
          <div className="rec-hero-right">
            <img src={recommendGame.icon} alt={recommendGame.gameName} className="rec-hero-img" />
          </div>
        </div>
      )}

      {/* Section Label */}
      <div className="games-section-header">
        <span className="games-section-title">{t('games.allGames')}</span>
      </div>

      {/* Grid */}
      <div className="games-grid">
        {sortedGames.map((game) => {
          const hasScore          = game.isPlayed && game.cognitiveScore > 0;
          const score             = hasScore ? game.cognitiveScore : null;
          // Never played, or played but no score (0) -- the card the user
          // still needs to act on, so it gets its own "to do" look.
          const needsPlay         = !hasScore;
          const isLastPlayedGame  = lastPlayed && game.gid === lastPlayed.gid;
          // recommendGame comes back from the API without a `gid` field --
          // only `gameId` (a plain numeric id, unlike the base64-ish `gid`
          // strings on `games[]`/`lastPlayed`) identifies which game it is.
          const isRecommendedGame = recommendGame && game.gameId === recommendGame.gameId;
          const playedDateTime    = isLastPlayedGame && lastPlayed?.playedAt
            ? lastPlayed.playedAt
            : game.lastPlayedLogDateTime;

          return (
            <div
              key={game.gid}
              className={`game-card${needsPlay ? ' game-card--todo' : ''}${isRecommendedGame ? ' game-card--highlight' : ''}`}
              onClick={() => handleGameClick(game.url, game.gameName)}
            >
              {isRecommendedGame && (
                <span className="highlight-badge" aria-label={t('games.recommended')}>⚡</span>
              )}

              <h3 className="game-title">{game.gameName}</h3>

              <span className="game-category">{game.category}</span>

              <div className="circle-wrap">
                <CircleRing score={score} bgColor={game.bgColor || BRAND_COLOR} gid={game.gid} todo={needsPlay} />
                <div className="circle-icon-inner">
                  <img src={game.icon} alt={game.gameName} className="circle-icon-img" />
                </div>
                {needsPlay && (
                  <span className="todo-play-dot" aria-hidden="true">
                    <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 1.2 L8.6 5 L2.5 8.8 Z" fill="currentColor" /></svg>
                  </span>
                )}
              </div>

              <span className="card-score">
                {hasScore
                  ? <span><strong style={{ color: game.bgColor || BRAND_COLOR }}>{game.cognitiveScore}</strong><span className="score-denom">/100</span></span>
                  : <span className="score-unplayed">--<span className="score-denom">/100</span></span>
                }
              </span>

              <span className={`game-play-status${game.isPlayed ? ' game-play-status--played' : ''}`}>
                {formatPlayedAgo(playedDateTime, t) || t('games.neverPlayed')}
              </span>

              <button
                className={`play-btn${needsPlay ? ' play-btn--todo' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleGameClick(game.url, game.gameName); }}
                aria-label={`Play ${game.gameName}`}
              >
                {t('games.play')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GamesList;
